import FocalPoint from "components/FocalPoint";
import getAdPlaying from "selectors/ads/getAdPlaying";
import getIsMobileSafari from "selectors/device/getIsMobileSafari";
import getPlayAd from "selectors/ads/getPlayAd";
import getPlaybackState from "selectors/chapter/getPlaybackState";
import getReceivedUserInteraction from "selectors/device/getReceivedUserInteraction";
import getRequireVerseThree from "selectors/loading/getRequireVerseThree";
import makeGetPosterUrl from "selectors/video/makeGetPosterUrl";
import makeGetThreeVideoConfig from "selectors/video/makeGetThreeVideoConfig";
import classnames from "classnames";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { autobind } from "core-decorators";
import { connect } from "react-redux";
import * as PlaybackStateActions from "actions/PlaybackStateActions";
import FocalPointImage from "components/FocalPointImage";
import * as ImageSize from "utils/ImageSize";
import fromRenderProps from "recompose/fromRenderProps";
import { compose } from "recompose";
import styles from "./videoPoster/VideoPoster.css";
import { WithActiveChapterId } from "../../context/ActiveChapterContext";

class VideoPoster extends Component {
  static propTypes = {
    allowPoster: PropTypes.bool.isRequired,
    alwaysCover: PropTypes.bool,
    cachedPosterImage: PropTypes.string,
    className: PropTypes.string,
    focalPoint: PropTypes.shape({
      aspectRatio: PropTypes.number,
      focalX: PropTypes.number.isRequired,
      focalY: PropTypes.number.isRequired,
      height: PropTypes.number,
      width: PropTypes.number,
    }).isRequired,
    ignorePlayerOptionFitMode: PropTypes.bool,
    initialTime: PropTypes.number.isRequired,
    is360: PropTypes.bool.isRequired,
    manualContainerHeight: PropTypes.number,
    manualContainerWidth: PropTypes.number,
    onOrientationUpdated: PropTypes.func.isRequired,
    posterUrl: PropTypes.string.isRequired,
    requireVerseThree: PropTypes.bool.isRequired,
    showAdPoster: PropTypes.bool.isRequired,
    threeConfig: PropTypes.object,
    time: PropTypes.number.isRequired,
    videoId: PropTypes.number.isRequired,
  };

  imageRef = React.createRef();

  constructor(props) {
    super(props);

    this.state = {
      focalPointIncludingImageDimensions: props.focalPoint,
    };

    this.initialTime = props.initialTime;
    if (props.requireVerseThree && !window.VerseThree) {
      import(/* webpackChunkName: "verse-three" */ "verse-three").then(
        (verseThree) => {
          window.VerseThree = verseThree;
          this.attachScene();
        },
      );
    }
  }

  componentWillUnmount() {
    this.cleanupPosterScene();
  }

  cleanupPosterScene() {
    if (this.posterScene) {
      this.posterScene.destroy();
      this.posterScene = null;
    }
  }

  @autobind
  sceneRefUpdated(el) {
    if (el) {
      this.el = el;
      this.attachScene();
    }
  }

  @autobind
  attachScene() {
    if (window.VerseThree.VideoPosterScene && this.el && !this.posterScene) {
      this.posterScene = new window.VerseThree.VideoPosterScene(this.el);
      this.posterScene.setHandlers({
        onOrientationUpdated: this.props.onOrientationUpdated,
      });
      this.posterScene.updateConfig(this.props.threeConfig);
      this.posterScene.start();
    }
  }

  render() {
    if (!this.props.allowPoster) {
      return null;
    }

    if (this.props.is360) {
      return (
        <div className={styles.root}>
          <FocalPoint fitMode="contain" focalPoint={this.props.focalPoint}>
            <div ref={this.sceneRefUpdated} />
          </FocalPoint>
        </div>
      );
    }

    return (
      <div className={classnames(styles.root, this.props.className)}>
        <FocalPointImage
          cachedDataUrl={this.props.cachedPosterImage}
          containThreshold={this.props.alwaysCover ? undefined : Infinity}
          debug="video poster"
          fitMode="cover"
          focalPoint={this.state.focalPointIncludingImageDimensions}
          manualContainerHeight={this.props.manualContainerHeight}
          manualContainerWidth={this.props.manualContainerWidth}
          size={ImageSize.LARGE}
          src={this.props.posterUrl}
          videoId={this.props.videoId}
        />
      </div>
    );
  }
}

const makeMapStateToProps = () => {
  const getPosterUrl = makeGetPosterUrl();
  const getThreeVideoConfig = makeGetThreeVideoConfig();

  return (state, ownProps) => {
    const playbackState = getPlaybackState(state, ownProps);
    const useAd = getPlayAd(state, ownProps) || getAdPlaying(state, ownProps);
    const actualTimeOffset = ownProps.timeOffset || 0;
    const usePlaybackTimeForInitialTime =
      getIsMobileSafari(state, ownProps) || !!playbackState.posterImage;
    const initialTime = usePlaybackTimeForInitialTime
      ? playbackState.time
      : actualTimeOffset;
    const video = state.story.entities.videos[ownProps.videoId];
    const is360 = Boolean(video.is360);

    return {
      allowPoster: !useAd || !ownProps.activeChapterId,
      cachedPosterImage: playbackState.posterImage,
      showAdPoster:
        getPlayAd(state, ownProps) &&
        !getAdPlaying(state, ownProps) &&
        getReceivedUserInteraction(state, ownProps),
      playing: playbackState.playing,
      posterUrl: getPosterUrl(state, ownProps),
      requireVerseThree: getRequireVerseThree(state, ownProps),
      time: playbackState.time,
      initialTime,
      is360,
      threeConfig: getThreeVideoConfig(state, ownProps),
    };
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onOrientationUpdated: (orientation) =>
    dispatch(
      PlaybackStateActions.changePlaybackState(ownProps, { orientation }),
    ),
});

const connector = connect(makeMapStateToProps, mapDispatchToProps);

export default compose(
  fromRenderProps(
    WithActiveChapterId,
    ({ activeChapterId }) => activeChapterId,
  ),
  connector,
)(VideoPoster);
